import {
  Modal,
  Flex,
  Box,
  H2,
  H5,
  Button,
  Loading,
  Notification,
  Select,
} from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useMemo, useState, useCallback } from "react";
import { useMutation, useQuery } from "@apollo/client";

import GroupTypeBadge from "src/components/Badge/GroupTypeBadge";
import TeamTypeOptionLabel from "src/pages/Workspaces/components/TagTypeConfig/TeamTypeOptionLabel";
import {
  getTeamChildTeamsQuery,
  getGroupQuery,
} from "src/queries/group.graphql";
import { updateGroupType } from "src/queries/mutations/group.graphql";
import {
  getAllowedTypeIds,
  getGroupedTypeOptions,
} from "../utils/changeTeamTypeOptions";

const ChangeTeamTypeModal = ({
  team,
  groupTypes,
  isVisible,
  onHideModal,
  runGroupCacheUpdateOnAddition,
}) => {
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState(null);

  const { data: childData } = useQuery(getTeamChildTeamsQuery, {
    variables: { groupId: team?.id },
    skip: !team?.id || !isVisible,
  });
  const { data: parentData } = useQuery(getGroupQuery, {
    variables: { groupId: team?.directParentId },
    skip: !team?.directParentId || !isVisible,
  });

  const childTeams = useMemo(
    () => childData?.team?.childTeams ?? [],
    [childData?.team?.childTeams]
  );
  const parentGroup = parentData?.getGroup ?? null;

  const allowedTypeIds = useMemo(
    () => getAllowedTypeIds(team, groupTypes, parentGroup, childTeams),
    [team, groupTypes, parentGroup, childTeams]
  );

  const groupedOptions = useMemo(
    () => getGroupedTypeOptions(groupTypes, allowedTypeIds),
    [groupTypes, allowedTypeIds]
  );

  const flatOptions = useMemo(
    () => groupedOptions.flatMap((g) => g.options),
    [groupedOptions]
  );

  const [updateGroupTypeMutation] = useMutation(updateGroupType, {
    refetchQueries: runGroupCacheUpdateOnAddition
      ? [{ query: getGroupQuery, variables: { groupId: team?.id } }]
      : [],
  });

  const onCancel = useCallback(() => {
    setSelectedTypeId("");
    setError(null);
    onHideModal();
  }, [onHideModal]);

  const onSubmit = useCallback(async () => {
    if (!selectedTypeId || isExecuting) {
      return;
    }
    setIsExecuting(true);
    setError(null);
    try {
      await updateGroupTypeMutation({
        variables: { groupId: team.id, type: selectedTypeId },
      });
      onCancel();
    } catch (err) {
      setError(err?.message || "Failed to update team type");
    } finally {
      setIsExecuting(false);
    }
  }, [
    selectedTypeId,
    isExecuting,
    team?.id,
    updateGroupTypeMutation,
    onCancel,
  ]);

  const canSubmit = selectedTypeId && !isExecuting;

  return (
    <Modal
      visible={isVisible}
      onClose={onCancel}
      width="90vw"
      maxWidth="500px"
      headerContent={
        <>
          <H5 weight="bold" color="greyDark">
            CHANGE TEAM TYPE
          </H5>
          <Flex alignItems="center">
            <H2 mr="r">{team?.name}</H2>
            <GroupTypeBadge group={team} groupTypes={groupTypes} />
          </Flex>
        </>
      }
      data-testid="cp-change-team-type-modal"
      footerContent={
        <Flex alignItems="center" justifyContent="flex-end" width="100%">
          <Button
            p="s"
            mr="s"
            onClick={onCancel}
            variant="ghost"
            disabled={isExecuting}
          >
            Cancel
          </Button>
          <Button
            p="s"
            onClick={onSubmit}
            variant={canSubmit ? "success" : "disabled"}
            disabled={!canSubmit}
            data-testid="cp-change-team-type-submit"
          >
            {isExecuting && <Loading mr="s" />}
            {isExecuting ? "Updating…" : "Update type"}
          </Button>
        </Flex>
      }
    >
      <Box my="r">
        <Box mb="s">
          Choose a new type in the same category (demand, supply, or other).
          Only types allowed by the parent and valid for existing child teams
          are shown.
        </Box>
        {groupedOptions.length === 0 ? (
          <Notification variant="warning" closable={false}>
            No other team types are available for this team under the current
            rules.
          </Notification>
        ) : (
          <Select
            ariaLabel="Select new team type"
            options={groupedOptions}
            value={flatOptions.find((o) => o.value === selectedTypeId) || null}
            onChange={(opt) => setSelectedTypeId(opt?.value ?? "")}
            formatOptionLabel={(option) => (
              <TeamTypeOptionLabel
                option={option}
                groupTypes={groupTypes}
                showTypeDescription={false}
              />
            )}
            placeholder="Select type"
            isClearable
            isSearchable
            data-testid="cp-change-team-type-select"
            menuPortalTarget={
              typeof document !== "undefined" ? document.body : null
            }
            menuPosition="fixed"
            updateStyles={(s) => ({
              ...s,
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            })}
          />
        )}
        {error && (
          <Notification mt="s" variant="danger" closable={false}>
            {error}
          </Notification>
        )}
      </Box>
    </Modal>
  );
};

ChangeTeamTypeModal.propTypes = {
  team: PropTypes.object.isRequired,
  groupTypes: PropTypes.object.isRequired,
  isVisible: PropTypes.bool.isRequired,
  onHideModal: PropTypes.func.isRequired,
  runGroupCacheUpdateOnAddition: PropTypes.bool,
};

export default React.memo(ChangeTeamTypeModal);
